import { useMemo, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { QueueDetailPane } from "./queue-detail-pane";
import { PaneActions, type PaneCommand } from "./pane-actions";
import {
  CreateIssueDialog,
  LinkExistingDialog,
} from "@/components/workflow/create-issue-dialog";
import {
  TriageActionDialog,
  type TriageAction,
} from "@/components/experience/triage-action-dialog";
import { useTransitionFindings } from "@/components/experience/use-transition-findings";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { useProjectRole } from "@/lib/use-project-role";
import { trpc } from "@/lib/trpc";
import { projectExportConfigSchema } from "@iamu/shared";
import type { QueueRowData } from "./queue-types";
import type { RouteFindingEvidence } from "@/components/pages-view/route-findings-list";

export interface FindingDetailDrawerProps {
  projectId: string;
  findingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fallbackRow?: Partial<QueueRowData> | null;
  routeEvidence?: RouteFindingEvidence | null;
  onTransitionSuccess?: () => void;
}

interface DialogState {
  action: TriageAction;
  ids: string[];
  subject: string;
}

export function FindingDetailDrawer({
  projectId,
  findingId,
  open,
  onOpenChange,
  fallbackRow,
  routeEvidence,
  onTransitionSuccess,
}: FindingDetailDrawerProps) {
  const { canEdit } = useProjectRole(projectId);
  const requests = useWorkRequests(projectId);
  const transition = useTransitionFindings(projectId);

  const [createIssueCanonicalId, setCreateIssueCanonicalId] = useState<
    string | null
  >(null);
  const [linkExistingCanonicalId, setLinkExistingCanonicalId] = useState<
    string | null
  >(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const { data: projectData } = trpc.project.getById.useQuery(
    { id: projectId },
    { enabled: open },
  );
  const { data: projectContext } = trpc.project.getContext.useQuery(
    { projectId },
    { enabled: open },
  );
  const { data: members } = trpc.project.members.useQuery(
    { projectId },
    { enabled: open },
  );
  const { data: detail } = trpc.finding.getById.useQuery(
    { id: findingId ?? "" },
    { enabled: Boolean(open && findingId) },
  );

  const row: QueueRowData = useMemo(() => {
    const rawDetail = detail as
      | {
          effectiveTitle?: string;
          effectiveDescription?: string;
          effectiveType?: string;
          effectiveSeverity?: string;
          state?: string;
          stateReason?: string | null;
          stateNote?: string | null;
          observationCount?: number;
          firstSeenAt?: string;
          lastSeenAt?: string;
          priorityScore?: number | null;
          priorityOverride?: string | null;
          assigneeId?: string | null;
          scenarioName?: string | null;
          personaName?: string | null;
          scenarioId?: string | null;
          effectiveWalkthroughId?: string;
          effectiveStepNumber?: number;
          effectiveTargetBox?: unknown;
          pageUrl?: string;
        }
      | undefined;

    const observedAtIso = routeEvidence?.observedAt
      ? typeof routeEvidence.observedAt === "string"
        ? routeEvidence.observedAt
        : routeEvidence.observedAt.toISOString()
      : "";

    return {
      id: findingId ?? "",
      title:
        rawDetail?.effectiveTitle ??
        fallbackRow?.title ??
        "Finding observation",
      preview:
        rawDetail?.effectiveDescription ??
        fallbackRow?.preview ??
        routeEvidence?.consequence ??
        routeEvidence?.uiFact ??
        null,
      type: rawDetail?.effectiveType ?? fallbackRow?.type ?? "problem",
      severity:
        rawDetail?.effectiveSeverity ?? fallbackRow?.severity ?? "major",
      state: rawDetail?.state ?? fallbackRow?.state ?? "open",
      stateReason: rawDetail?.stateReason ?? fallbackRow?.stateReason ?? null,
      stateNote: rawDetail?.stateNote ?? fallbackRow?.stateNote ?? null,
      snoozeUntil: null,
      snoozeTrigger: null,
      observationCount:
        rawDetail?.observationCount ?? fallbackRow?.observationCount ?? 1,
      firstSeenAt:
        rawDetail?.firstSeenAt ?? fallbackRow?.firstSeenAt ?? observedAtIso,
      lastSeenAt:
        rawDetail?.lastSeenAt ?? fallbackRow?.lastSeenAt ?? observedAtIso,
      priorityScore:
        rawDetail?.priorityScore ?? fallbackRow?.priorityScore ?? null,
      priorityOverride:
        rawDetail?.priorityOverride ?? fallbackRow?.priorityOverride ?? null,
      judgeRating: null,
      assigneeId: rawDetail?.assigneeId ?? fallbackRow?.assigneeId ?? null,
      outlineName: rawDetail?.scenarioName ?? fallbackRow?.outlineName ?? null,
      personaName:
        rawDetail?.personaName ??
        fallbackRow?.personaName ??
        routeEvidence?.persona?.name ??
        null,
      scenarioId: rawDetail?.scenarioId ?? fallbackRow?.scenarioId ?? null,
      groupKey: null,
      groupLabel: null,
      walkthroughId:
        routeEvidence?.walkthroughId ??
        rawDetail?.effectiveWalkthroughId ??
        fallbackRow?.walkthroughId ??
        null,
      stepNumber:
        routeEvidence?.stepNumber ??
        rawDetail?.effectiveStepNumber ??
        fallbackRow?.stepNumber ??
        null,
      targetBox:
        routeEvidence?.targetBox ??
        rawDetail?.effectiveTargetBox ??
        fallbackRow?.targetBox ??
        null,
      pageUrl:
        routeEvidence?.pageUrl ??
        rawDetail?.pageUrl ??
        fallbackRow?.pageUrl ??
        null,
    };
  }, [findingId, detail, fallbackRow, routeEvidence]);

  const handleCommand = (c: PaneCommand) => {
    if (!findingId) return;

    if (c === "accept") {
      transition.run({ ids: [findingId], to: "open" }, "Accepted", () => {
        onTransitionSuccess?.();
      });
      return;
    }
    if (c === "createIssue") {
      setCreateIssueCanonicalId(findingId);
      return;
    }
    if (c === "linkIssue") {
      setLinkExistingCanonicalId(findingId);
      return;
    }
    if (c === "dismiss") {
      transition.run(
        { ids: [findingId], to: "dismissed", reason: "not_relevant" },
        "Dismissed",
        () => {
          onTransitionSuccess?.();
        },
      );
      return;
    }
    if (c === "duplicate") {
      setDialog({
        action: "duplicate",
        ids: [findingId],
        subject: row.title ?? "This finding",
      });
    }
  };

  const actions = (
    <PaneActions
      row={row}
      canEdit={canEdit}
      projectId={projectId}
      hasLinkedRepo={Boolean(projectData?.githubRepositoryId)}
      requests={requests}
      members={members}
      onCommand={handleCommand}
    />
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-6 sm:max-w-xl lg:max-w-2xl"
          showCloseButton={false}
        >
          <SheetTitle className="sr-only">Finding detail</SheetTitle>
          <QueueDetailPane
            projectId={projectId}
            row={row}
            actions={actions}
            onClose={() => onOpenChange(false)}
            exportConfig={
              projectContext?.exportConfig
                ? projectExportConfigSchema.parse(projectContext.exportConfig)
                : undefined
            }
            hasLinkedRepo={Boolean(projectData?.githubRepositoryId)}
          />
        </SheetContent>
      </Sheet>

      {createIssueCanonicalId && (
        <CreateIssueDialog
          canonicalId={createIssueCanonicalId}
          open={Boolean(createIssueCanonicalId)}
          onOpenChange={(isOpen) => !isOpen && setCreateIssueCanonicalId(null)}
        />
      )}

      {linkExistingCanonicalId && (
        <LinkExistingDialog
          canonicalId={linkExistingCanonicalId}
          open={Boolean(linkExistingCanonicalId)}
          onOpenChange={(isOpen) => !isOpen && setLinkExistingCanonicalId(null)}
        />
      )}

      {dialog && (
        <TriageActionDialog
          action={dialog.action}
          ids={dialog.ids}
          projectId={projectId}
          subject={dialog.subject}
          onClose={() => setDialog(null)}
          onDone={() => {
            onTransitionSuccess?.();
          }}
        />
      )}
    </>
  );
}
